/**
 * Public Route Component
 *
 * Ensures public pages (landing, login, register) redirect authenticated users
 * to the appropriate destination:
 * - If not unlocked → /unlock
 * - If unlocked → /dashboard
 *
 * Non-authenticated users see the public page content.
 */

import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth, useEncryption } from '@/contexts'
import { Loading } from '@/components/common'

interface PublicRouteProps {
  children: ReactNode
}

const PublicRoute = ({ children }: PublicRouteProps) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { isUnlocked, isLoading: encryptionLoading } = useEncryption()
  const location = useLocation()

  // Show loading while checking auth/encryption status
  if (authLoading || (isAuthenticated && encryptionLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-pink-light via-white to-primary-pink-soft dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <Loading size="lg" />
      </div>
    )
  }

  // If authenticated, redirect based on encryption state
  if (isAuthenticated) {
    // Allow /register with invitation params through — Register component
    // will sign out the stale session before proceeding with registration
    const params = new URLSearchParams(location.search)
    if (location.pathname === '/register' && params.has('id') && params.has('token')) {
      return <>{children}</>
    }

    // Redirect to unlock screen if app is locked
    if (!isUnlocked) {
      return <Navigate to="/unlock" replace />
    }

    // Fully authenticated and unlocked - redirect to dashboard
    return <Navigate to="/dashboard" replace />
  }

  // Not authenticated - render public content
  return <>{children}</>
}

export default PublicRoute
